import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SkuWizard } from '@/components/SkuWizard';
import { invalidateNumberPolicy } from '@/components/registration';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 台帳ごとの「番号の採り方」の既定（2026-06・§8.10）。
 *
 * 登録フォームはここを読んで最初の選択肢を決める。ユーザーはフォーム側で
 * いつでも別のルールに変えられる（ここは**既定**だけ）。
 * 共通採番（EQP/CSM）の系列は1本のカウンターだけが刻む。自分の台帳の
 * 部署/種類 ルール（技術資産→`ENG/CSM`・IT資産→`IT/EQP`）は既定に選べるが、
 * 番号はそのカウンターから出る（新しいカウンターは作らない・飛び番も出ない）。
 */
export default function NumberPolicyCard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');
  const [pickFor, setPickFor] = useState(null);

  const load = () => axios.get(`${API}/sku/number-policy`)
    .then((r) => setRows(r.data?.registers || []))
    .catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async (register, mode, template_code) => {
    setBusy(register);
    try {
      await axios.put(`${API}/sku/number-policy`, { register, mode, template_code });
      invalidateNumberPolicy();
      await load();
      toast.success('既定の採番ルールを変更しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '変更に失敗しました');
    } finally { setBusy(''); setPickFor(null); }
  };

  const picking = useMemo(() => rows.find((r) => r.register === pickFor) || null, [rows, pickFor]);
  const seriesOf = (r) => (r.shared_prefixes || []).map((p) => ({
    prefix: p,
    label: r.series_labels?.[p] || p,
    next: r.shared_next?.[p] || '',
    rule: r.series_templates?.[p]?.name || '',
  }));

  return (
    <Card data-testid="number-policy-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">台帳ごとの既定の採番ルール</CardTitle>
        <p className="text-[12px] text-zinc-500">
          各台帳の「登録」画面が最初に選ぶ採り方です。登録する人はその場で別のルールに変えられます。
          共通採番（EQP/CSM）は1本のカウンターだけが刻みます。自分の台帳の 部署/種類 ルール
          （技術資産なら ENG/CSM・ENG/EQP、IT資産なら IT/EQP・IT/CSM）は既定に選べますが、
          番号は同じカウンターから出ます（新しいカウンターは作りません）。
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r) => {
          const series = seriesOf(r);
          const fromShared = r.issues_from === 'shared';
          return (
            <div key={r.register}
              className="rounded-lg border border-zinc-200 px-3 py-2"
              data-testid={`number-policy-row-${r.register}`}>
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-44">
                  <p className="text-sm font-semibold text-zinc-800">{r.label}</p>
                  <p className="text-[11px] text-zinc-500">{r.screen}</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {series.length > 0 && (
                    <button type="button" disabled={busy === r.register}
                      onClick={() => save(r.register, 'shared', null)}
                      className={`rounded-full border px-3 py-1 text-left text-[11px] font-semibold ${
                        fromShared
                          ? 'border-teal-600 bg-teal-600 text-white'
                          : 'border-zinc-200 bg-white text-zinc-600 hover:border-teal-300'}`}
                      data-testid={`number-policy-${r.register}-shared`}>
                      共通採番
                      {series.map((s, i) => (
                        <span key={s.prefix} className="ml-1.5 opacity-90"
                          data-testid={`number-policy-${r.register}-next-${s.prefix}`}>
                          {i > 0 && <span className="mr-1.5 opacity-50">・</span>}
                          {s.label} 次 <span className="font-mono">{s.next || '—'}</span>
                        </span>
                      ))}
                    </button>
                  )}
                  <Button type="button"
                    variant={r.mode === 'template' && !fromShared ? 'default' : 'outline'}
                    className="h-7 rounded-full px-3 text-[11px]"
                    disabled={busy === r.register} onClick={() => setPickFor(r.register)}
                    data-testid={`number-policy-${r.register}-template`}>
                    {r.mode === 'template'
                      ? (fromShared
                        ? '系列ごとのルールを選ぶ'
                        : `ルール: ${r.template_name || r.template_code || '未選択'}`)
                      : '品番ルールにする'}
                  </Button>
                </div>
                {!series.length && (
                  <span className="text-[11px] text-zinc-400">この台帳に共通採番の系列はありません</span>
                )}
              </div>

              {series.some((s) => s.rule) && (
                <div className="mt-1.5 flex flex-wrap gap-1.5 border-t border-zinc-100 pt-1.5"
                  data-testid={`number-policy-${r.register}-series-rules`}>
                  {series.filter((s) => s.rule).map((s) => (
                    <span key={s.prefix}
                      className="rounded border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] text-zinc-600"
                      data-testid={`number-policy-${r.register}-rule-${s.prefix}`}>
                      {s.label}: <b>{s.rule}</b>
                      <span className="ml-1 font-mono text-zinc-400">次 {s.next || s.prefix}</span>
                    </span>
                  ))}
                  <span className="text-[10px] text-zinc-400">
                    番号は共通採番のカウンターから出ます
                  </span>
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="text-sm text-zinc-500" data-testid="number-policy-empty">読み込み中…</p>
        )}
      </CardContent>

      <SkuWizard open={!!pickFor} onOpenChange={(o) => !o && setPickFor(null)} listAll pickOnly
        defaultApp={picking?.app_code || ''}
        deptLabel={picking?.dept_label || ''}
        sharedSeries={picking ? seriesOf(picking) : []}
        onIssued={(_sku, meta) => {
          if (!meta?.template_code) {
            toast.error('ルールを選び直してください');
            return;
          }
          save(pickFor, 'template', meta.template_code);
        }} />
    </Card>
  );
}
